import { createStore } from "zustand/vanilla";
import { playSfx } from "../sound/sfx";
import type { DmConnector, DmMessage, DmRoomHandle } from "./dm";

/**
 * Connection status of a chat surface:
 * - `connecting`   — the initial join is in flight;
 * - `open`         — live;
 * - `reconnecting` — the lifecycle is re-establishing a dropped socket
 *                    (transient inline notice; composer muted);
 * - `failed`       — the backoff attempts were exhausted (or the initial join
 *                    failed) — the dead-end, WITH the manual «تلاش دوباره» unless
 *                    {@link ChatState.canRetry} says the refusal was TERMINAL;
 * - `error`        — the server REFUSED a send (typed `dm-error`, e.g. BLOCKED)
 *                    or errored the room — sticky until the user leaves.
 */
export type ChatStatus = "connecting" | "open" | "reconnecting" | "failed" | "error";

export interface ChatState {
  status: ChatStatus;
  messages: DmMessage[];
  error: string | null;
  /**
   * COULD RETRYING POSSIBLY WORK? False only for a refusal we KNOW is terminal.
   *
   * ⚠️ IT EXISTS BECAUSE `failed` WAS OFFERING «تلاش دوباره» FOR A TABLE THAT NO LONGER EXISTS.
   * Table-chat membership is registered by the game room and cleared when it disposes, so
   * `TABLE_CHAT_NOT_A_MEMBER` means THE TABLE IS GONE — no amount of retrying brings a disposed
   * table back. The connector already knew that on the RE-acquire path (`isTableGone` →
   * `SeatExpiredError`, which ends the lifecycle cycle silently); the INITIAL join did not, so a
   * player whose table had ended was shown a button that could only ever fail again. Measured on
   * prod: 12 error groups in 6 days on the current bundle alone.
   */
  canRetry: boolean;
  /** Older messages exist beyond the oldest one held — drives the scroll-up loader. */
  hasMore: boolean;
  /** A page of older messages is in flight (so the sentinel does not fire again). */
  loadingOlder: boolean;
  /** True until the FIRST page has landed — the thread's own skeleton, not the socket's. */
  loadingHistory: boolean;
  send: (body: string) => void;
  /** Send an already-uploaded voice note into the conversation. */
  sendVoice: (voiceId: string, durationMs: number) => void;
  /** Fetch one page older than the oldest message held. No-op while one is in flight,
   *  when nothing older exists, or when this surface has no history source. */
  loadOlder: () => void;
  /** Manual retry from `failed`: restart the lifecycle's backoff cycle — or
   *  re-run the whole connect when the initial join never succeeded. */
  retry: () => void;
  disconnect: () => void;
}

/** One page of history, fetched over HTTP (see the api's `/me/dm/:peerId/history`). */
export type HistoryLoader = (before?: string) => Promise<{
  messages: DmMessage[];
  hasMore: boolean;
}>;

/**
 * Framework-free chat store (DM thread + table chat): connects via the injected
 * connector, seeds from history, then appends live messages. Deduplicates by id
 * so the sender's own echo (broadcast back by the room) is not shown twice.
 *
 * Reconnection: the real connectors are lifecycle-managed; their `onConnection`
 * stream drives `status` (reconnecting/open/failed), and a rejoin re-delivers
 * `onHistory`, which REPLACES the message list wholesale (DMs re-serve the
 * last 50 — covering the dead window; table chat re-seeds empty). Sends while
 * not `open` are refused by the UI (composer disabled) and, as a backstop,
 * dropped safely here (`safeSend` under a torn room / no handle).
 */
export function createChatStore(
  connector: DmConnector,
  /** OUR app user id. Only used to tell an incoming line from our own echo when
   *  deciding whether to chime; omit and the store stays silent. */
  opts: {
    selfId?: string;
    /**
     * HTTP history for this surface, if it has one (DMs do; table chat does not).
     *
     * ⚠️ ITS PRESENCE ALSO SWITCHES `onHistory` FROM REPLACE TO MERGE, and that is the whole
     * reason a socket rejoin does not undo paging: the room re-serves its newest 50 on every
     * join, so a wholesale replace would throw away every older page the user had scrolled up
     * to. Table chat keeps the old replace semantics untouched — it has no pages to lose, and
     * its rejoin deliberately re-seeds empty.
     */
    loadHistory?: HistoryLoader;
  } = {},
) {
  let handle: DmRoomHandle | null = null;
  /** The surface was torn down — a join still in flight must not be adopted
   *  (it would keep an unmanaged socket, or a managed one auto-reconnecting). */
  let disposed = false;

  /**
   * Union two message lists by id, in reading order.
   *
   * The two sources overlap by design — the HTTP page and the room's join history both end at
   * the newest message — so this is the seam that keeps them from doubling up. Sorted by `at`
   * because an older PAGE arrives after the newer ones it belongs before.
   */
  const mergeById = (a: DmMessage[], b: DmMessage[]): DmMessage[] => {
    const byId = new Map<string, DmMessage>();
    for (const m of a) byId.set(m.id, m);
    for (const m of b) byId.set(m.id, m);
    return [...byId.values()].sort((x, y) => x.at - y.at);
  };

  /** Add a message; TRUE only when it was genuinely new (not a re-delivered echo). */
  const append = (message: DmMessage): boolean => {
    let added = false;
    store.setState((s) => {
      if (s.messages.some((m) => m.id === message.id)) return s;
      added = true;
      return { messages: [...s.messages, message] };
    });
    return added;
  };

  const connect = (): void => {
    connector({
      // (Re)joins REPLACE the list wholesale. A sticky `error` (refused send)
      // outranks the transport state — it still gets the fresh messages, but
      // stays on its dead-end until the user backs out.
      //
      // SILENT, ALWAYS. This is the history — nothing has "arrived". The chime used to
      // be decided in the message list from renders alone, which cannot tell a history
      // load from a live line: the thread mounts EMPTY, the history lands as a change,
      // and so OPENING any conversation whose last line was the other person's played
      // the received-message sound at you. The store knows the difference; the UI does not.
      onHistory: (messages) =>
        store.setState((s) => {
          // MERGE when this surface pages (DMs), REPLACE when it does not (table chat) — see
          // `loadHistory`. Merging is what lets a reconnect land without discarding the older
          // pages the reader scrolled up to.
          const next = opts.loadHistory ? mergeById(s.messages, messages) : messages;
          return s.status === "error"
            ? { messages: next, loadingHistory: false }
            : { messages: next, status: "open", loadingHistory: false };
        }),
      // A LIVE line — the only thing that may chime, and never our own echo.
      onMessage: (message) => {
        if (!append(message)) return; // a duplicate: nothing actually arrived
        if (opts.selfId !== undefined && message.senderId !== opts.selfId) playSfx("msgReceive");
      },
      onError: (message) => store.setState({ status: "error", error: message }),
      onConnection: (status) =>
        store.setState((s) => {
          if (s.status === "error") return s;
          return status === "connected" ? { status: "open" } : { status };
        }),
    })
      .then((h) => {
        if (disposed) {
          // Torn down while the join was in flight — close the fresh room so it
          // neither lingers nor keeps auto-reconnecting.
          h.leave();
          return;
        }
        handle = h;
        store.setState((s) => (s.status === "error" ? s : { status: "open" }));
      })
      .catch((e: unknown) => {
        // The INITIAL join failed (nothing is lifecycle-managed yet): land on
        // `failed`, whose manual retry re-runs this whole connect — UNLESS the connector told us
        // the refusal is terminal, in which case there is nothing to re-run. See
        // {@link ChatState.canRetry}. The connector raises `SeatExpiredError` for that, exactly
        // as it already did on the re-acquire path.
        if (disposed) return;
        store.setState({
          status: "failed",
          error: e instanceof Error ? e.message : "connection failed",
          canRetry: !(e instanceof Error && e.name === "SeatExpiredError"),
        });
      });
  };

  const store = createStore<ChatState>(() => ({
    status: "connecting",
    canRetry: true,
    messages: [],
    error: null,
    hasMore: false,
    loadingOlder: false,
    // A surface WITHOUT an HTTP source has nothing of its own to wait for — its messages come
    // with the join, so it must not sit behind a history skeleton that will never resolve.
    loadingHistory: Boolean(opts.loadHistory),
    loadOlder: () => {
      const { loadHistory } = opts;
      const s = store.getState();
      if (!loadHistory || s.loadingOlder || !s.hasMore || s.messages.length === 0) return;
      // The cursor is the OLDEST message we hold — a keyset, so nothing is re-read and the page
      // does not drift as new messages land at the other end.
      const before = new Date(s.messages[0]?.at ?? 0).toISOString();
      store.setState({ loadingOlder: true });
      void loadHistory(before)
        .then((page) => {
          if (disposed) return;
          store.setState((cur) => ({
            messages: mergeById(page.messages, cur.messages),
            hasMore: page.hasMore,
            loadingOlder: false,
          }));
        })
        .catch(() => {
          // A failed page is not a dead end: leave `hasMore` alone so scrolling up tries again.
          if (!disposed) store.setState({ loadingOlder: false });
        });
    },
    send: (body) => {
      playSfx("msgSend");
      const trimmed = body.trim();
      if (trimmed) handle?.say(trimmed);
    },
    sendVoice: (voiceId, durationMs) => {
      playSfx("msgSend");
      handle?.sayVoice(voiceId, durationMs);
    },
    retry: () => {
      const state = store.getState();
      if (state.status !== "failed") return;
      // A terminal refusal has nothing to re-run. The UI hides the button, and this is the second
      // door — a `retry()` reached any other way (a stale handler, a test) must be inert too.
      if (!state.canRetry) return;
      if (handle?.retry) {
        // Lifecycle-managed connection: report it dead → a fresh backoff cycle.
        store.setState({ status: "reconnecting", error: null, canRetry: true });
        handle.retry();
      } else {
        // Never connected (or a handle without retry support): full re-connect.
        handle?.leave();
        handle = null;
        store.setState({ status: "connecting", error: null, messages: [], canRetry: true });
        connect();
      }
    },
    disconnect: () => {
      disposed = true;
      handle?.leave();
      handle = null;
    },
  }));

  // THE FIRST PAGE GOES OUT BEFORE THE SOCKET, not after it.
  //
  // This is the change that makes a conversation open quickly: one authenticated GET paints the
  // thread while the room join — matchmake, WS upgrade, onAuth, onJoin — proceeds behind it. The
  // socket is still what carries live send/receive; it is simply no longer the thing standing
  // between a tap and seeing the conversation.
  if (opts.loadHistory) {
    void opts
      .loadHistory()
      .then((page) => {
        if (disposed) return;
        store.setState((s) => ({
          messages: mergeById(page.messages, s.messages),
          // A live message may already have landed while this was in flight; it can only be
          // NEWER than the page, so the page's own `hasMore` still describes the older end.
          hasMore: page.hasMore,
          loadingHistory: false,
        }));
      })
      .catch(() => {
        // The socket's join history is the fallback — this failing must not blank the thread.
        if (!disposed) store.setState({ loadingHistory: false });
      });
  }

  connect();

  return store;
}

export type ChatStore = ReturnType<typeof createChatStore>;
